const COUNTRY_CODES = (
	'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY ' +
	'BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK ' +
	'FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR ' +
	'IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ' +
	'ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM ' +
	'PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF ' +
	'TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
).split(' ');

let regionNames: Intl.DisplayNames | null = null;
try {
	regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
} catch {
	regionNames = null;
}

export const countryName = (countryCode: string): string => {
	try {
		return regionNames?.of(countryCode.toUpperCase()) ?? countryCode;
	} catch {
		return countryCode;
	}
};

export type CountryOption = {
	code: string;
	name: string;
};

export const COUNTRY_OPTIONS: CountryOption[] = COUNTRY_CODES.map((code) => ({
	code: code,
	name: countryName(code),
})).sort((a, b) => a.name.localeCompare(b.name));

const REGIONS_BY_COUNTRY: Record<string, string[]> = {
	US: [
		'Alabama',
		'Alaska',
		'Arizona',
		'Arkansas',
		'California',
		'Colorado',
		'Connecticut',
		'Delaware',
		'District of Columbia',
		'Florida',
		'Georgia',
		'Hawaii',
		'Idaho',
		'Illinois',
		'Indiana',
		'Iowa',
		'Kansas',
		'Kentucky',
		'Louisiana',
		'Maine',
		'Maryland',
		'Massachusetts',
		'Michigan',
		'Minnesota',
		'Mississippi',
		'Missouri',
		'Montana',
		'Nebraska',
		'Nevada',
		'New Hampshire',
		'New Jersey',
		'New Mexico',
		'New York',
		'North Carolina',
		'North Dakota',
		'Ohio',
		'Oklahoma',
		'Oregon',
		'Pennsylvania',
		'Rhode Island',
		'South Carolina',
		'South Dakota',
		'Tennessee',
		'Texas',
		'Utah',
		'Vermont',
		'Virginia',
		'Washington',
		'West Virginia',
		'Wisconsin',
		'Wyoming',
		'American Samoa',
		'Guam',
		'Northern Mariana Islands',
		'Puerto Rico',
		'U.S. Virgin Islands',
	],
	CA: [
		'Alberta',
		'British Columbia',
		'Manitoba',
		'New Brunswick',
		'Newfoundland and Labrador',
		'Northwest Territories',
		'Nova Scotia',
		'Nunavut',
		'Ontario',
		'Prince Edward Island',
		'Quebec',
		'Saskatchewan',
		'Yukon',
	],
	AU: [
		'Australian Capital Territory',
		'New South Wales',
		'Northern Territory',
		'Queensland',
		'South Australia',
		'Tasmania',
		'Victoria',
		'Western Australia',
	],
	GB: ['England', 'Northern Ireland', 'Scotland', 'Wales'],
	IN: [
		'Andaman and Nicobar Islands',
		'Andhra Pradesh',
		'Arunachal Pradesh',
		'Assam',
		'Bihar',
		'Chandigarh',
		'Chhattisgarh',
		'Dadra and Nagar Haveli and Daman and Diu',
		'Delhi',
		'Goa',
		'Gujarat',
		'Haryana',
		'Himachal Pradesh',
		'Jammu and Kashmir',
		'Jharkhand',
		'Karnataka',
		'Kerala',
		'Ladakh',
		'Lakshadweep',
		'Madhya Pradesh',
		'Maharashtra',
		'Manipur',
		'Meghalaya',
		'Mizoram',
		'Nagaland',
		'Odisha',
		'Puducherry',
		'Punjab',
		'Rajasthan',
		'Sikkim',
		'Tamil Nadu',
		'Telangana',
		'Tripura',
		'Uttar Pradesh',
		'Uttarakhand',
		'West Bengal',
	],
};

export const regionsForCountry = (countryCode: string): string[] | null => REGIONS_BY_COUNTRY[countryCode] ?? null;

const regionOfLanguageTag = (language: string, likely: boolean): string | null => {
	try {
		const locale = new Intl.Locale(language);
		return (likely ? locale.maximize() : locale).region ?? null;
	} catch {
		return null;
	}
};

const firstKnownRegion = (languages: readonly string[], likely: boolean): string | null => {
	for (const language of languages) {
		const region = regionOfLanguageTag(language, likely);
		if (region !== null && COUNTRY_CODES.includes(region)) {
			return region;
		}
	}
	return null;
};

export const guessCountryCode = (languages: readonly string[]): string =>
	firstKnownRegion(languages, false) ?? firstKnownRegion(languages, true) ?? 'US';

export type SearchLocation = {
	countryCode: string | null;
	state: string | null;
};

export const NO_SEARCH_LOCATION: SearchLocation = { countryCode: null, state: null };

export const isKnownCountryCode = (code: string): boolean => COUNTRY_CODES.includes(code);

export const describeSearchLocation = (location: SearchLocation): string | null => {
	if (location.countryCode === null) {
		return null;
	}
	const country = countryName(location.countryCode);
	return location.state ? `${location.state}, ${country}` : country;
};
